import { type FieldValues } from 'react-hook-form';

import { APIS } from '~constants/index';
import { useLocalStorage, type UserStorage } from '~hooks/index';
import { getSidebarData } from '~layouts/Sidebar/SidebarRequests';
import { useAppStorage } from '~store/useStore';
import { sendRequest } from '~utils/index';
import { type ModalsLogin } from './LoginPage';

export interface CredentialTokens {
  access?: string;
  refresh?: string;
}

export const getPatientData = async (data: Record<string, unknown>) => {
  const { updateUserData } = useLocalStorage();

  updateUserData(data);
  await getUserInfo(updateUserData);
};

const getUserInfo = async (updateUserData: (newUserData: object) => void) => {
  const { requestFinalized } = useAppStorage.getState();

  await sendRequest<UserStorage>({
    url: APIS.MANAGE_USERS.USERS,
    thenFunction: async (res) => {
      if (res.status === 200) {
        const newUserData = res.data;
        updateUserData(newUserData);
        await getSidebarData();
        requestFinalized();
      }
    }
  });
};

interface Tokens extends CredentialTokens {
  session_uuid?: string;
  qr_code?: string;
}
export interface Tenant {
  database_name: string;
  domain: string;
  name: string;
}
interface TenantAuthentication {
  tenants: Tenant[];
}

const findTenantByIdentifier = (
  tenants: Tenant[],
  identifier: string
): Tenant | undefined => {
  const normalizedId = identifier.toLowerCase();
  return tenants.find((tenant) => tenant.name.toLowerCase() === normalizedId);
};

interface UserValidationProps {
  data: FieldValues;
  setShowModal?: Setter<ModalsLogin>;
  setTenants?: Setter<SelectData<Tenant>[]>;
  setCurrentStep: Setter<number>;
  setQrImage: Setter<string | null>;
  setSessionId: Setter<string | null>;
  tenantIdentifier?: string;
  onTenantNotFound?: () => void;
}
export const getUserValidation = ({
  data,
  setShowModal,
  setTenants,
  setCurrentStep,
  setQrImage,
  setSessionId,
  tenantIdentifier,
  onTenantNotFound
}: UserValidationProps) => {
  const { doingRequest, requestFinalized } = useAppStorage.getState();
  const { updateUserData } = useLocalStorage();
  doingRequest();

  sendRequest<TenantAuthentication>({
    url: APIS.AUTHENTICATE_TENANTS,
    method: 'post',
    type: 'public',
    domainRequest: false,
    data,
    thenFunction: (res) => {
      if (res.data) {
        const tenants = res.data.tenants;

        if (tenantIdentifier && tenants.length > 0) {
          const tenantSelected = findTenantByIdentifier(
            tenants,
            tenantIdentifier
          );

          if (tenantSelected) {
            getUserValidationTenant(data, tenantSelected);
            return;
          }

          requestFinalized();
          onTenantNotFound?.();
          return;
        }

        if (tenants.length === 0) {
          getUserValidationNoTenant(
            data,
            setCurrentStep,
            setQrImage,
            setSessionId
          );
        } else if (tenants.length === 1) {
          const tenantSelected = res.data.tenants[0];
          getUserValidationTenant(data, tenantSelected);
        } else {
          requestFinalized();
          const tenantOptions = tenants
            .filter((tenant) => tenant.name !== 'default')
            .map((tenant) => ({ label: tenant.name, value: tenant }));
          updateUserData({
            availableTenants: res.data.tenants,
            userCredentials: data
          });
          setTenants?.(tenantOptions);
          setShowModal?.('tenant');
        }
      } else requestFinalized();
    }
  });
};

export const getUserValidationTenant = async (
  data: FieldValues,
  tenantSelected: Tenant,
  onSuccess?: () => void
) => {
  const { updateUserData } = useLocalStorage();
  updateUserData({ tenantSelected });

  await sendRequest<CredentialTokens>({
    url: APIS.LOGIN,
    method: 'post',
    type: 'public',
    data,
    thenFunction: (token_res) => {
      if (token_res?.data?.access) {
        getPatientData({ tokens: token_res.data });
        onSuccess?.();
      }
    }
  });
};

export const getCodeValidation = (data: object) => {
  const { doingRequest } = useAppStorage.getState();
  const { updateUserData } = useLocalStorage();
  doingRequest();

  sendRequest<CredentialTokens>({
    url: APIS.CORE.VERIFY_2FA,
    method: 'post',
    type: 'public',
    data,
    thenFunction: (res) => {
      if (res?.data?.access) {
        updateUserData({ tokens: res.data });
      }
      getPatientData({ ...res.data });
    }
  });
};

export const getUserValidationNoTenant = (
  data: FieldValues,
  setCurrentStep: Setter<number>,
  setQrImage: Setter<string | null>,
  setSessionId: Setter<string | null>
) => {
  const { doingRequest, requestFinalized } = useAppStorage.getState();
  const { updateUserData } = useLocalStorage();
  doingRequest();

  sendRequest<Tokens>({
    url: APIS.LOGIN,
    method: 'post',
    type: 'public',
    data,
    thenFunction: (token_res) => {
      if (token_res.data?.qr_code || token_res.data?.session_uuid) {
        setCurrentStep(1);
        setQrImage(token_res.data?.qr_code ?? null);
        setSessionId(token_res.data?.session_uuid ?? null);
        requestFinalized();
      } else {
        if (token_res?.data?.access) {
          updateUserData({ tokens: token_res.data });
        }
        getPatientData({ token: token_res.data });
      }
    },
    catchFunction: () => requestFinalized()
  });
};
